import { Button, message } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import axios from "axios";
import React, { useState } from "react";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

const SAMPLE_HEADERS = [
  "Discount Type",
  "Publisher Name",
  "Customer Name",
  "Supplier Name",
  "Discount Value",
  "Discount Unit",
  "Status",
  "Effective From",
  "Effective To",
];
const SAMPLE_ROWS = [
  ["sale", "Publisher One", "Customer One", "", "10", "percentage", "true", "2025-01-01", ""],
  ["purchase", "Publisher One", "", "Supplier One", "5", "percentage", "true", "2025-01-01", ""],
];

const downloadSampleFormat = () => {
  const ws = XLSX.utils.aoa_to_sheet([SAMPLE_HEADERS, ...SAMPLE_ROWS]);
  const colWidths = [
    { wch: 14 },
    { wch: 18 },
    { wch: 18 },
    { wch: 18 },
    { wch: 14 },
    { wch: 12 },
    { wch: 8 },
    { wch: 14 },
    { wch: 14 },
  ];
  ws["!cols"] = colWidths;
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Discounts");
  XLSX.writeFile(wb, "discount_master_import_format.xlsx");
};

const normalizeKey = (key) => {
  if (!key || typeof key !== "string") return "";
  const k = key.toString().trim().toLowerCase().replace(/\s+/g, "_");
  const map = {
    discount_type: "discount_type",
    type: "discount_type",
    publisher_id: "publisher_id",
    publisher_name: "publisher_name",
    publisher: "publisher_name",
    customer_id: "customer_id",
    customer_name: "customer_name",
    customer: "customer_name",
    supplier_id: "supplier_id",
    supplier_name: "supplier_name",
    supplier: "supplier_name",
    discount_value: "discount_value",
    value: "discount_value",
    discount_unit: "discount_unit",
    unit: "discount_unit",
    status: "status",
    effective_from: "effective_from",
    effective_to: "effective_to",
  };
  return map[k] || key.trim();
};

const parseCSV = (text) => {
  const lines = text.split(/\r?\n/).filter((line) => line.trim());
  if (lines.length < 2) return [];
  const headerLine = lines[0];
  const headers = headerLine.split(",").map((h) => normalizeKey(h.trim()));
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(",").map((v) => v.trim());
    const obj = {};
    headers.forEach((h, idx) => {
      if (h) obj[h] = values[idx] != null ? values[idx] : "";
    });
    if (obj.discount_type || obj.publisher_id || obj.publisher_name) rows.push(obj);
  }
  return rows;
};

const parseExcel = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheet = workbook.SheetNames[0];
        const sheet = workbook.Sheets[firstSheet];
        const json = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
        if (!json.length) {
          resolve([]);
          return;
        }
        const rawHeaders = json[0].map((h) => (h != null ? String(h) : ""));
        const headers = rawHeaders.map((h) => normalizeKey(h));
        const rows = [];
        for (let i = 1; i < json.length; i++) {
          const row = json[i];
          const obj = {};
          headers.forEach((h, idx) => {
            if (h) obj[h] = row[idx] != null ? String(row[idx]).trim() : "";
          });
          if (obj.discount_type || obj.publisher_id || obj.publisher_name) rows.push(obj);
        }
        resolve(rows);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsArrayBuffer(file);
  });
};

const toBool = (v) => {
  if (v === true || v === "true" || v === "1" || v === "yes" || v === "y") return true;
  if (v === false || v === "false" || v === "0" || v === "no" || v === "n") return false;
  return true;
};

const UploadDiscountMasterExcel = ({ onSuccess }) => {
  const [loader, setLoader] = useState(false);
  const [file, setFile] = useState(null);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    setFile(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      message.warning("Please select a CSV or Excel file.");
      return;
    }
    const fileName = (file.name || "").toLowerCase();
    const isCsv = fileName.endsWith(".csv");
    const isExcel = fileName.endsWith(".xlsx") || fileName.endsWith(".xls");
    if (!isCsv && !isExcel) {
      message.warning("Please upload a .csv, .xlsx, or .xls file.");
      return;
    }

    setLoader(true);
    try {
      let rows = [];
      if (isCsv) {
        const text = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(r.result);
          r.onerror = rej;
          r.readAsText(file);
        });
        rows = parseCSV(text);
      } else {
        rows = await parseExcel(file);
      }

      const payload = rows
        .map((row) => {
          const discount_type = String(row.discount_type || "").trim().toLowerCase();
          const publisher_id = row.publisher_id != null && row.publisher_id !== "" ? Number(row.publisher_id) : null;
          const customer_id =
            row.customer_id != null && row.customer_id !== "" ? Number(row.customer_id) : null;
          const supplier_id =
            row.supplier_id != null && row.supplier_id !== "" ? Number(row.supplier_id) : null;
          const publisher_name = [row.publisher_name, row.publisher].find((v) => v != null && String(v).trim() !== "") ? String(row.publisher_name || row.publisher || "").trim() : null;
          const customer_name = [row.customer_name, row.customer].find((v) => v != null && String(v).trim() !== "") ? String(row.customer_name || row.customer || "").trim() : null;
          const supplier_name = [row.supplier_name, row.supplier].find((v) => v != null && String(v).trim() !== "") ? String(row.supplier_name || row.supplier || "").trim() : null;
          const discount_value = parseFloat(row.discount_value);
          const discount_unit = String(row.discount_unit || "percentage").trim().toLowerCase();
          const status = toBool(row.status);
          const effective_from = row.effective_from || null;
          const effective_to = row.effective_to || null;
          const item = {
            discount_type: discount_type === "purchase" ? "purchase" : "sale",
            publisher_id: publisher_id || undefined,
            customer_id: discount_type === "purchase" ? null : (customer_id || undefined),
            supplier_id: discount_type === "sale" ? null : (supplier_id || undefined),
            discount_value: isNaN(discount_value) ? 0 : discount_value,
            discount_unit: discount_unit === "fixed" ? "fixed" : "percentage",
            status,
            effective_from,
            effective_to,
          };
          if (publisher_name) item.publisher_name = publisher_name;
          if (customer_name) item.customer_name = customer_name;
          if (supplier_name) item.supplier_name = supplier_name;
          return item;
        })
        .filter((r) => (r.publisher_id || r.publisher_name) && (r.discount_type === "sale" ? (r.customer_id || r.customer_name) : (r.supplier_id || r.supplier_name)));

      if (payload.length === 0) {
        message.warning(
          "No valid rows. Use either IDs or names: Discount Type (sale/purchase), Publisher (ID or Name), Customer (for sale), Supplier (for purchase), Discount Value, Discount Unit (percentage/fixed)."
        );
        setLoader(false);
        return;
      }

      const { data } = await axios.post("discount-master?query=createmany", payload);
      setLoader(false);
      setFile(null);
      e.target.reset();

      if (data.created !== undefined) {
        const errMsg = data.errors?.length ? ` ${data.errors.length} row(s) failed.` : "";
        toast.success(`Imported ${data.created} of ${data.total} discount(s).${errMsg}`);
      } else {
        toast.success(`Imported ${payload.length} discount(s) successfully.`);
      }
      if (onSuccess) onSuccess();
    } catch (err) {
      setLoader(false);
      const msg = err.response?.data?.message || err.message || "Import failed.";
      toast.error(msg);
    }
  };

  return (
    <div className="text-center mt-2">
      <Button
        type="default"
        icon={<DownloadOutlined />}
        onClick={downloadSampleFormat}
        className="mb-2"
      >
        Download sample format (.xlsx)
      </Button>
      <form onSubmit={handleSubmit}>
        <input
          className="form-control"
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleFileChange}
        />
        <br />
        <Button className="mt-2" type="primary" htmlType="submit" shape="round" loading={loader}>
          Import from Excel
        </Button>
      </form>
    </div>
  );
};

export default UploadDiscountMasterExcel;
